import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import ContactForm from '../components/ContactForm'
import { updatePageSEO } from '../lib/seo'

export default function Home() {
  const [pageData, setPageData] = useState(null)
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    window.scrollTo(0, 0)

    const fetchData = async () => {
      try {
        // Fetch page metadata and content
        const pageRes = await fetch('https://api.dev.auxo.dk/public/by-slug/forside')
        if (pageRes.ok) {
          const data = await pageRes.json()
          setPageData(data)
          
          if (data.meta_title || data.meta_description) {
            updatePageSEO(data.meta_title, data.meta_description)
          }
        }

        // Fetch projects for showcase
        const projectsRes = await fetch('https://api.dev.auxo.dk/public/content/galleri')
        if (projectsRes.ok) {
          const data = await projectsRes.json()
          // Take the first 3 projects for the homepage showcase
          setProjects(data.slice(0, 3))
        }
      } catch (err) {
        console.error('Error fetching CMS data:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const mainServices = [
    {
      title: 'Renovering af hus',
      desc: 'Vi udfører alt inden for husrenovering, ombygning og modernisering med fokus på kvalitet og gennemtænkte løsninger.',
      icon: 'fa-house-chimney-crack',
      href: '/ydelser/renovering-af-hus',
    },
    {
      title: 'Nyt tag',
      desc: 'Vi beskytter dit hjem med et stærkt og gennemarbejdet tag. Vi rådgiver om materialevalg og tagrenovering.',
      icon: 'fa-person-digging',
      href: '/ydelser/nyt-tag',
    },
    {
      title: 'Udskiftning af vinduer',
      desc: 'Nye vinduer efter mål, der forbedrer både komfort, indeklima, energiforbrug og boligens samlede udtryk.',
      icon: 'fa-window-restore',
      href: '/ydelser/udskiftning-af-vinduer',
    },
    {
      title: 'Opbygning af træterrasse',
      desc: 'Få en ny træterrasse i høj kvalitet, tilpasset dine behov og ønsker. Vi rådgiver om materialer og opbygning.',
      icon: 'fa-trowel-bricks',
      href: '/ydelser/opbygning-af-traeterrasse',
    },
  ]

  const values = [
    {
      title: 'Vi skaber den gode oplevelse',
      desc: 'Vi skaber tryghed ved at levere et solidt og professionelt stykke håndværk for vores kunder. Hos BAW Byg og Konstruktion har vi sans for kvalitet.',
      icon: 'fa-shield-heart',
    },
    {
      title: 'Vi er nemme at komme i kontakt med',
      desc: 'Vi er tæt på vores kunder, dvs. at vi sørger for at opretholde en tæt kundekontakt og det vil være nemt at få fat på os via. mobil og mail.',
      icon: 'fa-phone-volume',
    },
    {
      title: 'Vi tager os zeit til vores kunder',
      desc: 'Alle kunder er lige vigtige, store som små. For os handler det om at komme ud og levere god service, gøre vores kunder tilfredse og sørge for de har haft en god oplevelse.',
      icon: 'fa-clock',
    },
    {
      title: 'Perfektionistisk håndværk',
      desc: 'Vi har kunden i fokus ved enhver tømreropgave, uanset om det er store eller små projekter. Desuden lægger vi stor stolthed i vores opgaver som erfarne tømrere.',
      icon: 'fa-compass-drafting',
    },
  ]

  return (
    <div className="bg-white">
      {/* SECTION 1: HERO BANNER SECTION (Left-aligned, Vertically Centered, Industrial Vibe) */}
      <section className="relative min-h-[85vh] flex items-center bg-[#111111] text-white py-24 md:py-32 overflow-hidden">
        {/* Background image overlay */}
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/c68264d9-2499-44ef-9e49-6b5cd7a6c3b2/12-86-1319.jpg"
            alt="BAW Byg og Konstruktion"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Dark overlay mask */}
        <div className="absolute inset-0 bg-black/40"></div>

        <div className="relative mx-auto max-w-7xl px-6 md:px-12 w-full z-10">
          <div className="max-w-3xl space-y-6 text-left">
            <span className="text-sm font-extrabold uppercase tracking-widest text-brand-orange block">
              PROFESSIONELT TØMRERFIRMA &amp; BYGGETEKNIKER
            </span>
            
            <h1 className="text-3xl md:text-5xl lg:text-5xl font-extrabold tracking-tight leading-none text-white uppercase">
              TØMRER &amp; BYGGETEKNIKER <br />
              <span className="text-brand-orange">I ÉN LØSNING</span>
            </h1>
            
            <p className="text-slate-300 text-sm md:text-base max-w-2xl font-normal leading-relaxed">
              Går du med drømmen om at få en tømrer til at stå for renovering eller istandsættelse af boligen eller virksomheden? Alliér dig med en alsidig tømrermester, som varetager hele paletten af tømreropgaver.
            </p>
            
            <div className="pt-4 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="inline-block border-2 border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-white font-bold py-4 px-10 uppercase text-xs tracking-widest rounded-none transition-all duration-300 cursor-pointer"
              >
                FÅ ET TILBUD
              </Link>
              <a
                href="tel:+4529678107"
                className="inline-block border-2 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white font-bold py-4 px-10 uppercase text-xs tracking-widest rounded-none transition-all duration-300 cursor-pointer"
              >
                RING I DAG
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: FLOATING FEATURE CARDS SECTION (Overlapping Hero Section) */}
      <section className="relative z-20 bg-transparent">
        <div className="max-w-7xl mx-auto px-6 md:px-12 -mt-12 md:-mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 shadow-2xl border border-slate-200/50 hover:shadow-3xl transition-shadow duration-300">
            {/* Left Card: Our Company */}
            <div className="bg-white p-10 md:p-12 flex flex-col items-center text-center justify-between group transition-all duration-300">
              <div className="space-y-6">
                <div className="text-brand-blue text-5xl">
                  <i className="fa-solid fa-helmet-safety"></i>
                </div>
                <h3 className="text-slate-800 text-xl font-bold uppercase tracking-wider">
                  Vores Firma
                </h3>
                <p className="text-slate-500 text-xs md:text-sm leading-relaxed max-w-xs mx-auto">
                  BAW Byg og Konstruktion v/ Benjamin A. W. Petersen leverer solidt og professionelt tømrerarbejde i Egå, Aarhus og omegn.
                </p>
              </div>
              <div className="pt-8">
                <Link
                  to="/om-os"
                  className="text-brand-blue font-bold text-xs uppercase tracking-widest border-b border-brand-blue/30 hover:border-brand-blue pb-1 transition-all"
                >
                  LÆS MERE
                </Link>
              </div>
            </div>

            {/* Middle Card: Spotlight - All Services */}
            <div className="bg-[#222222] p-12 md:p-14 flex flex-col items-center text-center justify-between shadow-2xl relative z-10 border-y-4 md:border-y-0 md:border-x-4 border-brand-orange">
              <div className="space-y-6">
                <div className="text-brand-orange text-5xl">
                  <i className="fa-solid fa-compass-drafting"></i>
                </div>
                <h3 className="text-white text-xl font-bold uppercase tracking-wider">
                  Vores Ydelser
                </h3>
                <p className="text-slate-300 text-xs md:text-sm leading-relaxed max-w-xs mx-auto">
                  Vi varetager alt fra renovering af hus, nyt tag, udskiftning af vinduer til opbygning af træterrasser og totalentreprise.
                </p>
              </div>
              <div className="pt-8">
                <Link
                  to="/ydelser/renovering-af-hus"
                  className="text-brand-orange font-bold text-xs uppercase tracking-widest border-b border-brand-orange/30 hover:border-brand-orange pb-1 transition-all"
                >
                  SE YDELSER
                </Link>
              </div>
            </div>

            {/* Right Card: Contact Us */}
            <div className="bg-white p-10 md:p-12 flex flex-col items-center text-center justify-between group transition-all duration-300">
              <div className="space-y-6">
                <div className="text-brand-blue text-5xl">
                  <i className="fa-solid fa-phone-volume"></i>
                </div>
                <h3 className="text-slate-800 text-xl font-bold uppercase tracking-wider">
                  Kontakt Os
                </h3>
                <p className="text-slate-500 text-xs md:text-sm leading-relaxed max-w-xs mx-auto">
                  Få et uforpligtende tilbud på dit næste byggeprojekt. Vi svarer hurtigt på både telefon og mail.
                </p>
              </div>
              <div className="pt-8">
                <Link
                  to="/kontakt"
                  className="text-brand-blue font-bold text-xs uppercase tracking-widest border-b border-brand-blue/30 hover:border-brand-blue pb-1 transition-all"
                >
                  SKRIV TIL OS
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: WHITE CONTENT BAND - ASYMMETRICAL FEATURE FOCUS (Intro Section) */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 items-stretch">
          {/* Left Side: Large image with sharp corners matching the height of the text container */}
          <div className="lg:col-span-6 flex flex-col h-full">
            <div className="relative h-full flex flex-col">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/c68264d9-2499-44ef-9e49-6b5cd7a6c3b2/hero-bg-1-1347.jpg"
                alt="Tømrerarbejde udført af BAW Byg"
                className="rounded-none shadow-none border border-slate-200 w-full h-full min-h-[400px] lg:h-full lg:min-h-0 object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#222222] text-white p-8 rounded-none shadow-none hidden sm:block max-w-xs border-l-4 border-brand-orange">
                <span className="font-display font-extrabold text-4xl block mb-1 text-brand-orange">100%</span>
                <span className="text-xs font-bold uppercase tracking-wider leading-relaxed block text-slate-300">
                  Fokus på kvalitet, ordentlighed og godt håndværk
                </span>
              </div>
            </div>
          </div>

          {/* Right Side: Fully left-aligned content grouping */}
          <div className="lg:col-span-6 flex flex-col items-start text-left justify-center space-y-6">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange">
              TØMRER I EGÅ &amp; ÅRHUS
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#111111] tracking-tight leading-tight uppercase">
              Lad BAW Byg og Konstruktion stå for dit <span className="text-brand-orange">tømrerarbejde</span>
            </h2>
            <p className="text-slate-600 leading-relaxed text-sm md:text-base">
              Leder du efter en dygtig og professionel tømrer, som kan hjælpe dig med dit tømrerarbejde? Hvis ja, så er du kommet til helt rigtige sted. BAW Byg og Konstruktion er et professionelt og kvalitetsbevidst tømrerfirma med mange års erfaring bag sig, og vi kan hjælpe med enhver opgave inden for tømrerfaget. Vi holder til i Egå, men vi har gennem tiden hjulpet mange kunder i og omkring Aarhus, bl.a. Risskov og Brabrand, og i resten af Østjylland.
            </p>
            <p className="text-slate-600 leading-relaxed text-sm md:text-base">
              Hos BAW Byg og Konstruktion lægger vi stor vægt på den gode oplevelse hele vejen igennem. Du kan forvente solidt og gennemarbejdet håndværk, tæt dialog og en tømrer, der er nem at få fat i undervejs. Vi tager os tid til opgaven og til dig som kunde – uanset om der er tale om et mindre projekt eller en større renovering. For os handler det om kvalitet, grundighed og om at aflevere et resultat, vi kan stå inde for, og som du føler dig tryg ved fra start til slut.
            </p>
            <div className="pt-4">
              <Link
                to="/om-os"
                className="bg-[#222222] hover:bg-brand-orange text-white font-bold py-4 px-8 uppercase text-xs tracking-widest rounded-none transition-all inline-flex items-center gap-2"
              >
                Læs mere om os
                <i className="fa-solid fa-arrow-right text-xs text-brand-orange"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: STATS COUNTER RIBBON (Restyled to "Text steps" Design Reference) */}
      <section className="bg-[#111111] py-24 text-white border-y border-[#222222]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 space-y-6">
          {/* Row 1 - Highlighted */}
          <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-10 p-8 bg-[#222222] rounded-none transition-all duration-300 hover:brightness-125 hover:bg-[#222222]/80 cursor-pointer">
            <span className="text-5xl sm:text-7xl font-black text-brand-orange font-display leading-none select-none shrink-0">
              01
            </span>
            <div className="space-y-2">
              <span className="text-xs tracking-widest text-brand-orange font-bold uppercase block">Uddannelse</span>
              <h4 className="text-xl font-bold text-white uppercase tracking-wide">Byggetekniker &amp; Tømrer</h4>
              <p className="text-sm text-slate-400 leading-relaxed font-sans">
                Solid faglig baggrund sikrer gennemtænkte løsninger. Benjamin er uddannet både tømrer og byggetekniker, hvilket giver en unik forståelse for hele byggeprocessen og de tekniske konstruktioner.
              </p>
            </div>
          </div>

          {/* Row 2 */}
          <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-10 p-8 bg-transparent rounded-none transition-all duration-300 hover:bg-[#222222]/40 hover:brightness-125 cursor-pointer">
            <span className="text-5xl sm:text-7xl font-black text-brand-blue font-display leading-none select-none shrink-0">
              02
            </span>
            <div className="space-y-2">
              <span className="text-xs tracking-widest text-brand-blue font-bold uppercase block">Erfaring</span>
              <h4 className="text-xl font-bold text-white uppercase tracking-wide">Mange års alsidig erfaring</h4>
              <p className="text-sm text-slate-400 leading-relaxed font-sans">
                Ekspertise i alt fra mindre opgaver til totalentrepriser. Med mange års erfaring i tømrerfaget løser vi dine opgaver med høj præcision og sans for detaljen, uanset omfang.
              </p>
            </div>
          </div>

          {/* Row 3 */}
          <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-10 p-8 bg-transparent rounded-none transition-all duration-300 hover:bg-[#222222]/40 hover:brightness-125 cursor-pointer">
            <span className="text-5xl sm:text-7xl font-black text-brand-orange/40 font-display leading-none select-none shrink-0">
              03
            </span>
            <div className="space-y-2">
              <span className="text-xs tracking-widest text-brand-orange font-bold uppercase block">Kommunikation</span>
              <h4 className="text-xl font-bold text-white uppercase tracking-wide">Tæt kundekontakt</h4>
              <p className="text-sm text-slate-400 leading-relaxed font-sans">
                Nemme at fange og altid i tæt dialog under hele forløbet. Vi vægter den gode dialog højt, så du altid er fuldt opdateret på dit byggeprojekt fra start til slut.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: SERVICES SECTION WITH SECTION INTRODUCTION (Restyled to "design" Design Reference) */}
      <section className="py-24 bg-slate-50 border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          {/* Header Block */}
          <div className="text-left mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange block mb-2">
              HVAD TILBYDER VI?
            </span>
            <h2 className="text-3xl md:text-5xl font-light text-[#111111] uppercase tracking-tight">
              Vores vigtigste <span className="text-brand-orange font-normal">ydelser</span>
            </h2>
            <div className="w-16 h-1 bg-brand-orange mt-6"></div>
          </div>

          {/* Feature Grid (Minimal, Purely defined by white space, typography, and thin left borders) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {mainServices.map((s, idx) => (
              <div key={idx} className="border-l-2 border-brand-orange pl-6 py-2 flex flex-col justify-between h-full">
                <div className="space-y-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-brand-orange block">
                    YDELSE 0{idx + 1}
                  </span>
                  <h3 className="text-lg font-bold text-[#111111] uppercase tracking-wide">
                    {s.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed font-sans">
                    {s.desc}
                  </p>
                </div>
                <div className="pt-4">
                  <Link
                    to={s.href}
                    className="text-brand-orange hover:text-slate-800 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-1.5"
                  >
                    Læs mere
                    <i className="fa-solid fa-arrow-right text-[10px]"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-left mt-16">
            <Link
              to="/ydelser/renovering-af-hus"
              className="bg-[#222222] hover:bg-brand-orange text-white font-bold py-4 px-10 uppercase text-xs tracking-widest rounded-none transition-all shadow-none inline-flex items-center gap-2 cursor-pointer"
            >
              Se vores ydelser
              <i className="fa-solid fa-arrow-right text-xs text-brand-orange"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 7: VALUES SECTION (Restyled to "Features" Design Reference) */}
      <section className="relative py-24 text-white overflow-hidden">
        {/* Full-bleed background image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/c68264d9-2499-44ef-9e49-6b5cd7a6c3b2/12-86-1319.jpg"
            alt="Vores værdier baggrund"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/80"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Left Column: Headline & Attribution */}
          <div className="lg:col-span-5 space-y-6 text-left">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange block">
              VORES VÆRDIER
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight uppercase">
              Hvorfor vælge vores <span className="text-brand-orange">tømrerfirma?</span>
            </h2>
            <p className="text-slate-300 text-sm md:text-base leading-relaxed">
              Vi lægger stor vægt på kvalitetsbevidst arbejde, og rådgiver kunderne professionelt om den rigtige løsning. Hos os er du sikret ordentlighed og godt håndværk fra start til slut.
            </p>
            <div className="pt-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 text-brand-orange hover:text-white font-bold text-xs uppercase tracking-widest border-b border-brand-orange/30 hover:border-brand-orange pb-1 transition-all"
              >
                Få et uforpligtende tilbud i dag
                <i className="fa-solid fa-chevron-right text-[10px]"></i>
              </Link>
            </div>
          </div>

          {/* Right Column: 2x2 Grid of Square Cards */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {values.map((v, idx) => (
                <div
                  key={idx}
                  className="bg-[#333333]/40 backdrop-blur-md p-8 rounded-none border border-[#444444]/50 shadow-xl flex flex-col items-center text-center justify-center min-h-[260px]"
                >
                  <div className="bg-[#111111] text-brand-orange h-16 w-16 rounded-full flex items-center justify-center text-2xl border border-[#333333] mb-5 shrink-0">
                    <i className={`fa-solid ${v.icon}`}></i>
                  </div>
                  <h3 className="text-white text-sm font-bold uppercase tracking-wider mb-3">
                    {v.title}
                  </h3>
                  <p className="text-slate-300 text-xs md:text-sm italic leading-relaxed">
                    {v.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8: PROJECTS SHOWCASE SECTION (Sharp corners, clean borders) */}
      {projects.length > 0 && (
        <section className="py-24 bg-slate-50 border-t border-slate-200">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
              <div className="space-y-4">
                <span className="text-xs font-bold uppercase tracking-widest text-brand-orange block">TIDLIGERE ARBEJDE</span>
                <h2 className="text-3xl md:text-5xl font-extrabold text-[#111111] tracking-tight uppercase">
                  Udvalgte projekter &amp; <span className="text-brand-orange">referencer</span>
                </h2>
              </div>
              <div>
                <Link
                  to="/projekter"
                  className="bg-[#222222] hover:bg-brand-orange text-white font-bold py-4 px-8 uppercase text-xs tracking-widest rounded-none transition-all inline-flex items-center gap-2 cursor-pointer"
                >
                  Se alle projekter
                  <i className="fa-solid fa-images text-brand-orange"></i>
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {projects.map((p) => (
                <div key={p.id} className="bg-white rounded-none border border-slate-200 overflow-hidden flex flex-col h-full transition-all hover:border-brand-orange group">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,height=400,fit=cover,quality=80,format=auto/${p.images[0].replace('https://assets.auxodev.dk/', '')}`}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8 flex flex-col justify-between flex-grow">
                    <div className="space-y-3">
                      <h3 className="text-xl font-bold text-[#111111] uppercase tracking-wide">{p.title}</h3>
                      <div
                        className="text-slate-600 text-sm leading-relaxed line-clamp-3"
                        dangerouslySetInnerHTML={{ __html: p.description }}
                      />
                    </div>
                    <div className="pt-6 border-t border-slate-100 mt-6">
                      <Link
                        to="/projekter"
                        className="text-brand-orange hover:text-slate-800 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-1.5"
                      >
                        Se hele galleriet
                        <i className="fa-solid fa-arrow-right text-[10px]"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 9: CONTACT SECTION (Dark, premium background with background image and overlay) */}
      <section className="relative py-24 text-white overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/c68264d9-2499-44ef-9e49-6b5cd7a6c3b2/1-41.jpg"
            alt="BAW Byg baggrund"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/85"></div>
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5 space-y-8">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange block">KONTAKT OS I DAG</span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight uppercase">
              Lad os vende dine idéer og finde den <span className="text-brand-orange">rigtige løsning</span>
            </h2>
            <p className="text-slate-300 leading-relaxed text-sm md:text-base">
              Har du brug for en erfaren tømrer eller et tømrerfirma, der tager opgaven seriøst i Risskov, Lystrup, Skødstrup, Egå – og i særlige tilfælde også i Aarhus-området? Så er du altid velkommen til at kontakte BAW Byg og Konstruktion for en uforpligtende snak om dine idéer, dit projekt og hvad der giver bedst mening for dig.
            </p>
            
            <div className="bg-transparent p-8 rounded-none space-y-6">
              <div className="flex items-center gap-4">
                <div className="bg-[#111111] text-brand-orange h-12 w-12 rounded-none flex items-center justify-center text-lg shrink-0 border border-[#333333]">
                  <i className="fa-solid fa-phone"></i>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 block font-semibold uppercase tracking-widest">Ring direkte</span>
                  <a href="tel:+4529678107" className="font-bold text-white hover:text-brand-orange transition-colors whitespace-nowrap text-lg">
                    +45 2967 8107
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-[#111111] text-brand-orange h-12 w-12 rounded-none flex items-center justify-center text-lg shrink-0 border border-[#333333]">
                  <i className="fa-solid fa-envelope"></i>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 block font-semibold uppercase tracking-widest">Send en mail</span>
                  <a href="mailto:kontakt@baw-byg.dk" className="font-bold text-white hover:text-brand-orange transition-colors whitespace-nowrap text-lg">
                    kontakt@baw-byg.dk
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-[#111111] text-brand-orange h-12 w-12 rounded-none flex items-center justify-center text-lg shrink-0 border border-[#333333]">
                  <i className="fa-solid fa-location-dot"></i>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 block font-semibold uppercase tracking-widest">Adresse</span>
                  <span className="font-bold text-white text-lg block">
                    Øster Kringelvej 7, 8250 Egå
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 bg-transparent p-8 md:p-12">
            <ContactForm subject="Henvendelse fra Forsiden" dark={true} />
          </div>
        </div>
      </section>
    </div>
  )
}
